/**
 * HL-12777 — A/B test: the default product on Choose Treatment (DE WL Care+).
 *
 * Variant A (control): Mounjaro pen opens selected (ct_preselected_catalog_id).
 * Variant B: Wegovy pill opens selected.
 *
 * Weight is the `default_product_ab_test` variable in the variables table. The server
 * (choose-treatment-settings) checks eligibility — DE Care+, no product link, no earlier paid WL
 * order — and returns the weight plus each group's catalog only for enrolled visitors. The variant
 * is assigned once in ChooseTreatmentRouter and persisted in localStorage by ABTestManager. A signed-in
 * customer's group (`variant`, derived from the customer id on the server) is used only by a browser
 * that has no group yet; a browser's existing group never switches (the visitor may have seen it).
 */

import { ABTestManager } from "./ABTestManager";
import { MainTracker } from "../PixelTrackers/MainTracker";

export type DefaultProductVariant = "A" | "B";

export type DefaultProductTest = {
  weight: number;
  A: { catalog_id: number; slug: string | null };
  B: { catalog_id: number; slug: string | null };
  variant?: DefaultProductVariant | null;
};

export const DEFAULT_PRODUCT_TEST_NAME = "default_product";
const VARIANTS: readonly DefaultProductVariant[] = ["A", "B"];
const SLUG_KEY = `abtest_${DEFAULT_PRODUCT_TEST_NAME}_slug`;

// Mixpanel super property names (from the ticket).
const VARIANT_PROP = "Default Product Test";
const SLUG_PROP = "default_product_slug";

/**
 * Assign the variant using the server-provided weight for Group B and register the Mixpanel
 * super properties so every later event (through Paid Order) carries them. Call once from
 * ChooseTreatmentRouter, before the page view fires. Returns the catalog the variant opens with,
 * or null when not enrolled (no assignment, no properties).
 */
export const assignDefaultProductVariant = (test: DefaultProductTest | null | undefined): number | null => {
  if (!test) return null;

  // A group this browser already has wins: the visitor may have seen its product. The customer's group
  // only seeds a browser that has none yet (a new device, a fresh incognito window).
  const storedVariant = getDefaultProductVariant();
  const manager = new ABTestManager({
    testName: DEFAULT_PRODUCT_TEST_NAME,
    variants: [...VARIANTS],
    weights: [1 - test.weight, test.weight],
  });
  if (!storedVariant && test.variant && manager.getVariant() !== test.variant) manager.setVariant(test.variant);
  const variant: DefaultProductVariant = manager.getVariant() === "B" ? "B" : "A";

  const slug = test[variant].slug;
  if (slug) localStorage.setItem(SLUG_KEY, slug);
  else localStorage.removeItem(SLUG_KEY);
  MainTracker.superProperties(getDefaultProductTrackingProps());
  return test[variant].catalog_id;
};

/**
 * A DE WL Care+ load that is not enrolled (product link, returning customer, test off) drops the super
 * properties an earlier enrolled visit registered, so its later events up to Paid Order don't count for
 * that group (AC-8). The stored group stays: an enrolled visit later keeps it.
 */
export const clearDefaultProductTrackingProps = () => {
  try {
    localStorage.removeItem(SLUG_KEY);
  } catch {}
  MainTracker.unregisterSuperProperties([VARIANT_PROP, SLUG_PROP]);
};

/** Read the already-assigned variant; null if the visitor was never enrolled. */
export const getDefaultProductVariant = (): DefaultProductVariant | null =>
  ABTestManager.getStoredVariant(DEFAULT_PRODUCT_TEST_NAME, [...VARIANTS]) as DefaultProductVariant | null;

/** Props for event merges (CT page view). Empty object if not enrolled. */
export const getDefaultProductTrackingProps = (): Record<string, string> => {
  const variant = getDefaultProductVariant();
  if (!variant) return {};
  const slug = localStorage.getItem(SLUG_KEY);
  return { [VARIANT_PROP]: variant, ...(slug ? { [SLUG_PROP]: slug } : {}) };
};
